import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { MessageCircle, Send, X, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 4000;

const api = axios.create({ baseURL: API, withCredentials: true });

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [hasNewReply, setHasNewReply] = useState(false);
  const scrollerRef = useRef(null);
  const lastSeenCount = useRef(0);

  // On mount, check if a session already exists in the cookie
  useEffect(() => {
    let cancelled = false;
    api.get("/chat/me").then(
      ({ data }) => { if (!cancelled) setSession(data); },
      () => { /* no session yet, that's fine */ }
    );
    return () => { cancelled = true; };
  }, []);

  const loadMessages = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await api.get("/chat/messages");
      setMessages(data);
      // Detect new agent replies for badge
      const agentCount = data.filter((m) => m.from_role === "agent").length;
      if (!open && agentCount > lastSeenCount.current) setHasNewReply(true);
      if (open) lastSeenCount.current = agentCount;
    } catch (err) {
      /* ignore transient errors */
    }
  }, [session, open]);

  // Poll while session exists
  useEffect(() => {
    if (!session) return;
    loadMessages();
    const t = setInterval(loadMessages, POLL_MS);
    return () => clearInterval(t);
  }, [session, loadMessages]);

  // Auto-scroll to bottom on new messages while open
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
    if (open) {
      setHasNewReply(false);
      lastSeenCount.current = messages.filter((m) => m.from_role === "agent").length;
    }
  }, [messages, open]);

  const startSession = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return;
    setStarting(true);
    try {
      const { data } = await api.post("/chat/start", {
        name: name.trim(),
        email: email.trim() || null,
      });
      setSession(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not start chat. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setDraft("");
    try {
      const { data } = await api.post("/chat/messages", { body });
      setMessages((prev) => [...prev, data]);
    } catch (err) {
      console.error("send failed", err);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        style={{
          position: "fixed",
          bottom: 96,            // above the Made with Emergent badge
          right: 20,
          width: 60,
          height: 60,
          borderRadius: 30,
          background: "#0F2540",
          color: "#FFFFFF",
          border: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 18px 36px -12px rgba(15,37,64,0.55)",
          cursor: "pointer",
          zIndex: 10000,
          transition: "transform .2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {open ? <X size={22} strokeWidth={2} /> : <MessageCircle size={24} strokeWidth={1.8} />}
        {hasNewReply && !open && (
          <span
            style={{
              position: "absolute", top: 6, right: 6,
              width: 14, height: 14, borderRadius: 7,
              background: "#B68A35", border: "2px solid #FFFFFF",
            }}
          />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          style={{
            position: "fixed",
            bottom: 168,
            right: 20,
            width: 360,
            maxWidth: "calc(100vw - 40px)",
            height: 520,
            maxHeight: "calc(100vh - 200px)",
            background: "#FFFFFF",
            borderRadius: 18,
            boxShadow: "0 30px 80px -20px rgba(15,37,64,0.35)",
            border: "1px solid #E7E1D5",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 10000,
            fontFamily: "Manrope, system-ui, sans-serif",
          }}
        >
          {/* header */}
          <div style={{ background: "#0F2540", color: "#fff", padding: "16px 18px" }}>
            <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: 20, fontWeight: 600 }}>
              Chat with a Capless advisor
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
              We typically reply within a few minutes during business hours.
            </div>
          </div>

          {/* body */}
          {!session ? (
            <form onSubmit={startSession} style={{ padding: 20, flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ color: "#26425E", fontSize: 14, margin: 0 }}>
                Hi 👋 Tell us your name to get started.
              </p>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#26425E", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>
                  Your name <span style={{ color: "#B68A35" }}>*</span>
                </label>
                <input
                  data-testid="chat-name"
                  required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Jordan Cole"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E7E1D5", fontSize: 14, outline: "none", color: "#0F2540" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#26425E", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>
                  Email (optional)
                </label>
                <input
                  data-testid="chat-email"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="jordan@business.com"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E7E1D5", fontSize: 14, outline: "none", color: "#0F2540" }}
                />
              </div>
              {error && <div style={{ color: "#b00", fontSize: 13 }}>{error}</div>}
              <button
                data-testid="chat-start"
                type="submit" disabled={starting}
                style={{
                  background: "#B68A35", color: "#fff", border: "none",
                  padding: "12px 16px", borderRadius: 999, fontWeight: 600, fontSize: 14,
                  cursor: "pointer", opacity: starting ? 0.5 : 1,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                {starting ? "Starting…" : "Start chat"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#5B6B7E", fontSize: 11, marginTop: "auto" }}>
                <ShieldCheck size={12} /> Encrypted · we never share your info
              </div>
            </form>
          ) : (
            <>
              <div ref={scrollerRef} style={{ flex: 1, padding: 16, overflowY: "auto", background: "#FAF7F1" }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: "center", color: "#5B6B7E", fontSize: 13, padding: "20px 10px" }}>
                    👋 Hi {session.visitor_name}! Send your first message and an advisor will reply shortly.
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.from_role === "visitor";
                  return (
                    <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 10 }}>
                      <div style={{
                        maxWidth: "80%",
                        background: mine ? "#0F2540" : "#FFFFFF",
                        color: mine ? "#fff" : "#0F2540",
                        border: mine ? "none" : "1px solid #E7E1D5",
                        borderRadius: 14,
                        padding: "8px 12px",
                        fontSize: 14,
                        lineHeight: 1.45,
                      }}>
                        {!mine && (
                          <div style={{ fontSize: 10, color: "#B68A35", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 2 }}>
                            {m.from_name} · Capless
                          </div>
                        )}
                        <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
                        <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4, textAlign: "right" }}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={sendMessage} style={{ padding: 12, borderTop: "1px solid #E7E1D5", display: "flex", gap: 8, background: "#fff" }}>
                <input
                  data-testid="chat-input"
                  value={draft} onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E7E1D5", fontSize: 14, outline: "none", color: "#0F2540" }}
                />
                <button
                  data-testid="chat-send" type="submit" disabled={!draft.trim()}
                  style={{
                    background: "#0F2540", color: "#fff", border: "none",
                    padding: "0 14px", borderRadius: 10, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600,
                    opacity: draft.trim() ? 1 : 0.4,
                  }}
                >
                  <Send size={14} strokeWidth={2} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
